import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";
import { LogOut, Trophy, Copy, ExternalLink, User, Save, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * Customer's own history of every play/claim across CityDooh.
 *
 * Fetches /api/customer/my-winnings (auth required). Each entry
 * renders as a coupon-box card reusing the yellow-pill visual
 * treatment from PrizeRevealModal so the code / expiry / status feel
 * consistent with what the customer saw at win-time.
 */
export default function MyWinningsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const customerBlob = (() => {
    try {
      return JSON.parse(localStorage.getItem("citydooh_customer") || "{}");
    } catch {
      return {};
    }
  })();

  useEffect(() => {
    const tok = localStorage.getItem("citydooh_customer_token");
    if (!tok) {
      navigate("/customer/login");
      return;
    }
    api
      .customerMyWinnings()
      .then((r) => setItems(r.data || []))
      .catch((e) => {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          localStorage.removeItem("citydooh_customer_token");
          navigate("/customer/login");
        } else {
          setError("Couldn't load your winnings");
        }
      });
    // Load fresh profile from server so the edit form starts pre-filled.
    api.customerMe().then((r) => {
      const p = r.data || {};
      setProfile({ name: p.name || "", email: p.email || "", phone: p.phone || "" });
    }).catch(() => {});
  }, [navigate]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const r = await api.customerUpdateProfile({
        name: profile.name.trim() || null,
        phone: profile.phone.trim() || null,
      });
      const fresh = r.data || {};
      setProfile((p) => ({ ...p, name: fresh.name || p.name, phone: fresh.phone || p.phone }));
      localStorage.setItem("citydooh_customer", JSON.stringify(fresh));
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem("citydooh_customer_token");
    localStorage.removeItem("citydooh_customer");
    toast.success("Signed out");
    navigate("/");
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Coupon code copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const statusBadge = (s) => {
    if (s === "redeemed") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">Redeemed</Badge>;
    if (s === "expired") return <Badge variant="outline" className="bg-white text-[#0A0A0A]/60 border-2 border-[#0A0A0A]/30">Expired</Badge>;
    if (s === "no_prize") return <Badge variant="outline" className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A]">Better luck next time</Badge>;
    return <Badge className="bg-amber-100 text-amber-900 border-amber-200 hover:bg-amber-100">Not redeemed</Badge>;
  };

  const gameLabel = (t) =>
    t === "scratch_card" ? "Scratch Card" : t === "static_offer" ? "Instant Offer" : "Spin Wheel";

  return (
    <div className="min-h-screen bg-[#F7F4EC] text-[#0A0A0A] flex flex-col">
      {/* Brand chrome — same style as the login page so the two feel
          like one continuous "customer" surface. */}
      <header className="border-b-2 border-black bg-[#F7F4EC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="mywinnings-brand">
            <BrandMark size="header" />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/explore"
              className="text-xs sm:text-sm font-bold uppercase tracking-widest text-black hover:text-[#F5A800]"
            >
              Explore
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={signOut}
              className="border-2 border-black text-black hover:bg-[#F5A800] hover:text-black text-xs font-bold uppercase tracking-widest"
              data-testid="customer-signout"
            >
              <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest text-[#F5A800] font-black">
            {customerBlob?.email || "Signed in"}
          </p>
          <h1
            className="text-4xl md:text-5xl leading-none mt-2 flex items-center gap-3"
            style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
          >
            MY <span className="text-[#F5A800]">WIN</span>NINGS
            <Trophy className="h-8 w-8 md:h-10 md:w-10 text-[#F5A800]" />
          </h1>
        </div>

        {/* Account details — Section 1c. Name/phone editable; email
            is locked because it's the OTP login identifier. */}
        <Card className="p-5 bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] mb-8" style={{ boxShadow: "4px 4px 0 #0A0A0A" }} data-testid="profile-account-card">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-[#F5A800]" />
            <h2 className="text-xl uppercase leading-none" style={{ fontFamily: '"Anton","Impact",sans-serif' }}>Account</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-name">Full name</Label>
              <Input id="p-name" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} placeholder="Your name" data-testid="profile-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-phone">Phone</Label>
              <Input id="p-phone" value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} placeholder="+91…" data-testid="profile-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-email">Email (login)</Label>
              <Input id="p-email" value={profile.email} disabled data-testid="profile-email-input" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#0A0A0A] text-[#F5A800] hover:bg-[#F5A800] hover:text-[#0A0A0A] border-2 border-[#0A0A0A]" data-testid="profile-save">
              {savingProfile ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save changes
            </Button>
            <Button variant="outline" onClick={() => navigate("/explore")} className="border-2 border-[#0A0A0A] text-[#0A0A0A]" data-testid="profile-back-to-explore">Back to Explore</Button>
          </div>
        </Card>

        {error && (
          <Card className="p-6 text-center text-red-600 bg-white border-2 border-[#0A0A0A]">{error}</Card>
        )}

        {!error && items === null && (
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-5 animate-pulse h-32 bg-white/50" />
            ))}
          </div>
        )}

        {!error && items && items.length === 0 && (
          <Card className="p-12 text-center bg-white border-2 border-[#0A0A0A] text-[#0A0A0A]" data-testid="my-winnings-empty">
            <p className="font-heading text-lg mb-2 text-[#0A0A0A]">No winnings yet</p>
            <p className="text-sm text-[#0A0A0A]/60 mb-4">
              Play a spin wheel, scratch card or claim an offer and it&apos;ll show up here.
            </p>
            <Button onClick={() => navigate("/explore")}>Explore campaigns</Button>
          </Card>
        )}

        {items && items.length > 0 && (
          <div className="grid grid-cols-1 gap-4" data-testid="my-winnings-list">
            {items.map((w) => (
              <Card key={w.lead_id} className="p-5 bg-white border-2 border-[#0A0A0A] text-[#0A0A0A]" style={{ boxShadow: "4px 4px 0 #0A0A0A" }} data-testid={`winning-card-${w.lead_id}`}>
                <div className="flex items-start gap-4">
                  {w.thumbnail_url || w.vendor_logo ? (
                    <img
                      src={assetUrl(w.thumbnail_url || w.vendor_logo)}
                      alt=""
                      className="h-14 w-14 rounded-lg object-cover border border-border"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-lg bg-black flex items-center justify-center text-[var(--graffiti-yellow,#F5A800)] font-black text-xl">
                      {(w.vendor_business || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold">{w.vendor_business}</p>
                      <Badge
                        variant="outline"
                        className="text-[10px] font-black uppercase tracking-widest bg-[#0A0A0A] text-[#F5A800] border-2 border-[#0A0A0A] hover:bg-[#0A0A0A]"
                      >
                        {gameLabel(w.game_type)}
                      </Badge>
                      {statusBadge(w.redemption_status)}
                    </div>
                    <p className="text-sm text-[#0A0A0A]/60">{w.campaign_name}</p>
                    <p className="text-xl md:text-2xl mt-2 leading-none uppercase" style={{ fontFamily: '"Anton", "Impact", sans-serif' }}>{w.prize || "—"}</p>
                    {w.coupon_code && (
                      <div className="mt-3 flex items-center gap-2">
                        <div
                          className="inline-block bg-[var(--graffiti-yellow,#F5A800)] text-black font-mono font-bold text-lg tracking-widest px-4 py-2 rounded-lg"
                          data-testid="winning-coupon-code"
                        >
                          {w.coupon_code}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => copyCode(w.coupon_code)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        {w.redirect_url && (
                          <a
                            href={w.redirect_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-[var(--graffiti-yellow,#F5A800)] hover:underline"
                          >
                            Claim <ExternalLink className="h-3.5 w-3.5 ml-1" />
                          </a>
                        )}
                      </div>
                    )}
                    {w.expires_at && !w.is_better_luck && (
                      <p className="text-xs text-[#0A0A0A]/60 mt-2">
                        Expires {new Date(w.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
